// src/components/CreatePendentLoad.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt, FaClock, FaCheckCircle } from "react-icons/fa";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import Select from "react-select";
import axios from "../axiosConfig";
import CreatableSelect from "react-select/creatable";
import "../styles/CreatePendentLoad.css";
import { useTranslation } from "react-i18next";

const CreatePendentLoad = () => {
  const { t } = useTranslation("create_pendentload");

  const [length, setLength] = useState("");
  const [height, setHeight] = useState("");
  const [width, setWidth] = useState("");
  const [weight, setWeight] = useState("");
  const [merchandise, setMerchandise] = useState("");
  const [merchandiseError, setMerchandiseError] = useState("");
  const [price, setPrice] = useState("");
  const [priceCurrency, setPriceCurrency] = useState("EUR");
  const [priceError, setPriceError] = useState("");

  // 🆕 Price for subcontracted (only when external carrier selected)
  const [price4subcontracted, setPrice4subcontracted] = useState("");
  const [price4subcontractedError, setPrice4subcontractedError] = useState("");

  const [notes4driver, setNotes4driver] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [createdPendentLoadId, setCreatedPendentLoadId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleCloseSuccessPopup = () => {
    setSuccessPopupOpen(false);
    navigate("/loads?view=pendent", { replace: true });
  };
  const [loadBearer, setLoadBearer] = useState(false);
  const [clientType, setClientType] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [industryClients, setIndustryClients] = useState([]);
  const [carrierClients, setCarrierClients] = useState([]);
  const [specialLoad, setSpecialLoad] = useState(false);

  const [selectedClientContactEmails, setSelectedClientContactEmails] = useState([]);
  const [selectedSubcontractedEmails, setSelectedSubcontractedEmails] = useState([]);

  const normalizeMoney = (str) => {
    if (str == null) return "";

    const s = String(str).trim().replace(/\s+/g, "");
    const hasDot = s.includes(".");
    const hasComma = s.includes(",");

    let t2 = s.replace(/[^\d.,-]/g, "");

    if (hasDot && hasComma) {
      const last = Math.max(t2.lastIndexOf("."), t2.lastIndexOf(","));
      t2 = t2
        .split("")
        .filter((ch, i) => (ch === "." || ch === ",") ? i === last : true)
        .join("");
    }

    t2 = t2.replace(",", ".");
    return t2;
  };

  const formatPrice = (amount, currency = "EUR") => {
    if (amount == null || amount === "") return "";

    const num = Number(amount);
    if (Number.isNaN(num)) return String(amount);

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      }).format(num);
    } catch {
      return `${num.toFixed(2)} ${currency}`;
    }
  };

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
  };

  const extractClientEmails = (clientObj) => {
    if (!clientObj || typeof clientObj !== "object") return [];

    const found = new Set();

    const addEmail = (value) => {
      const email = String(value || "").trim().toLowerCase();
      if (!email) return;
      if (!isValidEmail(email)) return;
      found.add(email);
    };

    const contactsRaw = clientObj.contacts;
    let contacts = [];

    if (Array.isArray(contactsRaw)) {
      contacts = contactsRaw;
    } else if (typeof contactsRaw === "string") {
      try {
        const parsed = JSON.parse(contactsRaw);
        if (Array.isArray(parsed)) contacts = parsed;
      } catch {
        contacts = [];
      }
    }

    contacts.forEach((contact) => {
      addEmail(contact?.contactEmail);
      addEmail(contact?.email);
    });

    // fallback leve para campos de contacto no topo do cliente
    addEmail(clientObj.contactEmail);
    addEmail(clientObj.mainContactEmail);
    addEmail(clientObj.secondaryContactEmail);

    return Array.from(found).map((email) => ({
      value: email,
      label: email,
    }));
  };

  // 🆕 Trailer type (load requirement)
  const [trailerType, setTrailerType] = useState(null); // "ANY" | "TAUTLINER" | ... | "OTHER" | null
  const [trailerTypeOther, setTrailerTypeOther] = useState("");

  const [pickupPoints, setPickupPoints] = useState([
    {
      location: "",
      date: "",
      hour: "",
      hourError: false,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",
      lengthError: "",
      widthError: "",
      heightError: "",

      placeId: null,
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      workingHours: null,

      // 🆕 NEW
      loadingAccess: null,

      // 🆕 NEW (per point)
      handlingEquipment: null,
      handlingEquipmentOther: "",

      // 🆕 NEW (optional)
      reference: "",
    },
  ]);

  const [deliveryPoints, setDeliveryPoints] = useState([
    {
      location: "",
      date: "",
      hour: "",
      hourError: false,

      // 🆕 point-level dimensions
      length: "",
      width: "",
      height: "",
      lengthError: "",
      widthError: "",
      heightError: "",

      placeId: null,
      address: "",
      postalCode: "",
      city: "",
      country: "",
      gMapsLink: "",
      coordinates: "",
      contactPhone: "",
      workingHours: null,

      // 🆕 NEW
      loadingAccess: null,

      // 🆕 NEW (per point)
      handlingEquipment: null,
      handlingEquipmentOther: "",

      // 🆕 NEW (optional)
      reference: "",
    },
  ]);

  const inputRefs = useRef({});

  const closeAllNativePickers = (exceptKey = null) => {
    Object.entries(inputRefs.current).forEach(([key, el]) => {
      if (!el || key === exceptKey) return;

      const type = el.getAttribute?.("type");
      if (type === "date" || type === "time") {
        try {
          el.blur();
        } catch (err) {
          // ignore
        }
      }
    });
  };

  const queryParams = new URLSearchParams(location.search);
  const returnPath = queryParams.get("return") || "loads";
  const validViews = ["pendent", "current", "history"];
  const rawView = queryParams.get("view");
  const view = validViews.includes(rawView) ? rawView : "current";
  const [transportCarrierId, setTransportCarrierId] = useState(null);
  const [carrierError, setCarrierError] = useState(false);
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [invalidFields, setInvalidFields] = useState({});
  const [pickupFieldValidationError, setPickupFieldValidationError] = useState(
    false
  );
  const [deliveryFieldValidationError, setDeliveryFieldValidationError] =
    useState(false);
  // ✅ Only show global required messages after clicking "Create Load"
  const [didSubmit, setDidSubmit] = useState(false);
  const [lengthError, setLengthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [widthError, setWidthError] = useState("");
  const [weightError, setWeightError] = useState("");
  const [clientTypeError, setClientTypeError] = useState(false);
  const [selectedClientError, setSelectedClientError] = useState(false);
  const [deliveryPlaceError, setDeliveryPlaceError] = useState("");
  const [pickupBeforeDeliveryError, setPickupBeforeDeliveryError] =
    useState(false);
  const [globalHourFormatError, setGlobalHourFormatError] = useState(false);

  // ===================== Loading Access =====================
  const loadingAccessOptions = [
    { value: "SIDE", label: t("loadingAccess.options.side", { defaultValue: "Side" }) },
    { value: "REAR", label: t("loadingAccess.options.rear", { defaultValue: "Rear" }) },
    { value: "TOP",  label: t("loadingAccess.options.top",  { defaultValue: "Top" }) },
    { value: "DOCK", label: t("loadingAccess.options.dock", { defaultValue: "Dock" }) },
    { value: "ANY",  label: t("loadingAccess.options.any",  { defaultValue: "Any" }) },
  ];

  const handleLoadingAccessChange = (index, opt, isPickup) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    if (isPickup) {
      setPickupPoints((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], loadingAccess: value };
        return updated;
      });
    } else {
      setDeliveryPoints((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], loadingAccess: value };
        return updated;
      });
    }
  };

  // ===================== Handling Equipment =====================
  const handlingEquipmentOptions = [
    { value: "FORKLIFT", label: t("handlingEquipment.options.forklift", { defaultValue: "Forklift" }) },
    { value: "PALLET_JACK", label: t("handlingEquipment.options.palletJack", { defaultValue: "Pallet jack" }) },
    { value: "ELECTRIC_PALLET_JACK", label: t("handlingEquipment.options.electricPalletJack", { defaultValue: "Electric pallet jack" }) },
    { value: "CRANE", label: t("handlingEquipment.options.crane", { defaultValue: "Crane" }) },
    { value: "LOADING_RAMP", label: t("handlingEquipment.options.loadingRamp", { defaultValue: "Loading ramp" }) },
    { value: "TAIL_LIFT", label: t("handlingEquipment.options.tailLift", { defaultValue: "Tail lift" }) },
    { value: "HAND_LOAD", label: t("handlingEquipment.options.handLoad", { defaultValue: "Hand load" }) },
    { value: "CONVEYOR", label: t("handlingEquipment.options.conveyor", { defaultValue: "Conveyor" }) },
    { value: "OTHER", label: t("handlingEquipment.options.other", { defaultValue: "Other" }) },
  ];

  // ===================== Trailer Type =====================
  const trailerTypeOptions = [
    { value: "ANY", label: t("trailerType.options.ANY", { defaultValue: "Any" }) },
    { value: "TAUTLINER", label: t("trailerType.options.TAUTLINER", { defaultValue: "Tautliner / Curtain" }) },
    { value: "REEFER",    label: t("trailerType.options.REEFER", { defaultValue: "Reefer / Frigo" }) },
    { value: "BOX",       label: t("trailerType.options.BOX", { defaultValue: "Box" }) },
    { value: "MEGA",      label: t("trailerType.options.MEGA", { defaultValue: "Mega" }) },
    { value: "FLATBED",   label: t("trailerType.options.FLATBED", { defaultValue: "Flatbed" }) },
    { value: "TIPPER",    label: t("trailerType.options.TIPPER", { defaultValue: "Tipper" }) },
    { value: "LOWBED",    label: t("trailerType.options.LOWBED", { defaultValue: "Lowbed" }) },
    { value: "TANKER",    label: t("trailerType.options.TANKER", { defaultValue: "Tanker" }) },
    { value: "CONTAINER_CHASSIS", label: t("trailerType.options.CONTAINER_CHASSIS", { defaultValue: "Container chassis" }) },
    { value: "OTHER",     label: t("trailerType.options.OTHER", { defaultValue: "Other" }) },
  ];

  const handleHandlingEquipmentChange = (index, opt, isPickup) => {
    const value = opt?.value ? String(opt.value).trim().toUpperCase() : null;

    const apply = (prev) => {
      const updated = [...prev];
      const cur = { ...updated[index] };

      cur.handlingEquipment = value;

      // se não for OTHER, limpa o campo "other"
      if (value !== "OTHER") {
        cur.handlingEquipmentOther = "";
      }

      updated[index] = cur;
      return updated;
    };

    if (isPickup) {
      setPickupPoints(apply);
    } else {
      setDeliveryPoints(apply);
    }

    // limpar erros relacionados (se existirem)
    setInvalidFields((prev) => {
      const next = { ...prev };
      delete next[`${isPickup ? "pickup" : "delivery"}-handlingEquipment-${index}`];
      delete next[`${isPickup ? "pickup" : "delivery"}-handlingEquipmentOther-${index}`];
      return next;
    });
  };

  // ===== Working-hours helpers =====
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function weekdayKeyFromISO(isoDateStr) {
    if (!isoDateStr) return null;
    const d = new Date(isoDateStr);
    if (Number.isNaN(d)) return null;
    return DAYS[d.getDay()];
  }

  function parseWorkingHours(wh) {
    if (!wh) return null;
    if (typeof wh === "string") {
      try {
        return JSON.parse(wh);
      } catch {
        return null;
      }
    }
    if (typeof wh === "object") return wh;
    return null;
  }

  function formatIntervalsForDay(workingHours, dayKey) {
    if (!workingHours || !dayKey) return null;
    const day = workingHours[dayKey];
    if (!Array.isArray(day)) return null; // [["08:00","12:00"],["13:00","17:00"]]
    if (day.length === 0) return t("workingHours.noInfo");
    return day.map(([a, b]) => `${a}–${b}`).join(", ");
  }
  // ===== end helpers =====

  // Remover ponto de carga (Pick-Up)
  const removePickUpPlace = (indexToRemove) => {
    setPickupPoints((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Remover ponto de descarga (Delivery)
  const removeDeliveryPlace = (indexToRemove) => {
    setDeliveryPoints((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const checkPickupFieldErrors = (points) => {
    const errors = {};

    points.forEach((point, index) => {
      if (!point.location?.trim()) {
        errors[`pickup-location-${index}`] = t("errors.locationRequired");
      }

      ["length", "width", "height"].forEach((field) => {
        const value = point[field];
        const dimensionError = getPointDimensionError(field, value);

        if (dimensionError) {
          errors[`pickup-${field}-${index}`] = dimensionError;
        }
      });

      if (
        String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" &&
        !point.handlingEquipmentOther?.trim()
      ) {
        errors[`pickup-handlingEquipmentOther-${index}`] =
          t("errors.handlingEquipmentOtherRequired", {
            defaultValue: "❌ Please specify the handling equipment.",
          });
      }
    });

    const updatedPoints = points.map((point, index) => ({
      ...point,
      showMoreFields:
        !!errors[`pickup-handlingEquipmentOther-${index}`] ||
        point.showMoreFields,
    }));

    setPickupPoints(updatedPoints);
    return errors;
  };

  const checkDeliveryFieldErrors = (points) => {
    const errors = {};

    points.forEach((point, index) => {
      if (!point.location?.trim()) {
        errors[`delivery-location-${index}`] = t("errors.locationRequired");
      }

      ["length", "width", "height"].forEach((field) => {
        const value = point[field];
        const dimensionError = getPointDimensionError(field, value);

        if (dimensionError) {
          errors[`delivery-${field}-${index}`] = dimensionError;
        }
      });

      if (
        String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" &&
        !point.handlingEquipmentOther?.trim()
      ) {
        errors[`delivery-handlingEquipmentOther-${index}`] =
          t("errors.handlingEquipmentOtherRequired", {
            defaultValue: "❌ Please specify the handling equipment.",
          });
      }
    });

    const updatedPoints = points.map((point, index) => ({
      ...point,
      showMoreFields:
        !!errors[`delivery-handlingEquipmentOther-${index}`] ||
        point.showMoreFields,
    }));

    setDeliveryPoints(updatedPoints);
    return errors;
  };

  const clearFieldErrors = (index, type, fields = []) => {
    const updatedErrors = { ...invalidFields };
    fields.forEach((field) => {
      delete updatedErrors[`${type}-${field}-${index}`];
    });
    setInvalidFields(updatedErrors);
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const [industriesRes, carriersRes] = await Promise.all([
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);
        setIndustryClients(industriesRes.data);
        setCarrierClients(carriersRes.data);
      } catch (error) {
        console.error("❌ Error fetching clients:", error);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    let alive = true;
  
    (async () => {
      try {
        const res = await axios.get("/auth/whoami");
        if (!alive) return;
  
        const resolvedCompanyName = String(
          res.data?.company_name ||
          res.data?.companyName ||
          res.data?.name ||
          res.data?.company?.name ||
          ""
        ).trim();
  
        console.log("🏢 whoami company name resolved =", resolvedCompanyName, res.data);
  
        setCompanyName(resolvedCompanyName);
      } catch (err) {
        console.warn("❌ Error fetching company name:", err?.response?.status, err?.response?.data);
        setCompanyName("");
      }
    })();
  
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const fetchCarriers = async () => {
      try {
        const res = await axios.get("/carriers");

        const getCarrierName = (c) =>
          String(
            c?.companyName ||
            c?.name ||
            c?.company?.companyName ||
            c?.company?.name ||
            ""
          ).trim();

        const sorted = (res.data || []).sort((a, b) =>
          getCarrierName(a).localeCompare(getCarrierName(b), undefined, {
            sensitivity: "base",
          })
        );

        const internalLabel = companyName && String(companyName).trim()
        ? `${String(companyName).trim()} (internal)`
        : t("general.internalCarrier", { defaultValue: "Internal carrier" });
      
      const internalOpt = {
        value: "__internal__",
        label: internalLabel,
        isInternal: true,
        searchText: `${internalLabel} internal`.toLowerCase(),
      };
      
      const nextCarriers = [
        internalOpt,
        ...sorted.map((c) => ({
          value: String(c.id),
          label: getCarrierName(c),
          isInternal: false,
          searchText: getCarrierName(c).toLowerCase(),
        })),
      ];
        
        console.log("🚚 companyName =", companyName);
        console.log("🚚 internalOpt =", internalOpt);
        console.log("🚚 nextCarriers =", nextCarriers);
        
        setAvailableCarriers(nextCarriers);
      } catch (err) {
        console.error("❌ Error fetching carriers:", err);
      }
    };

    fetchCarriers();
  }, [companyName, t]);

  useEffect(() => {
    if (transportCarrierId != null && String(transportCarrierId).trim() !== "") return;
  
    const internal = (availableCarriers || []).find(
      (c) => c?.isInternal || String(c?.value) === "__internal__"
    );
  
    if (internal) {
      setTransportCarrierId(String(internal.value));
      setCarrierError(false);
    }
  }, [availableCarriers, transportCarrierId]);

  const [placeOptions, setPlaceOptions] = useState([]);

  const selectedTransportOptMemo = useMemo(() => {
    return (
      (availableCarriers || []).find(
        (c) => String(c.value) === String(transportCarrierId)
      ) || null
    );
  }, [availableCarriers, transportCarrierId]);

  const isInternalCarrierMemo = useMemo(() => {
    return (
      selectedTransportOptMemo?.isInternal ||
      String(selectedTransportOptMemo?.value) === "__internal__"
    );
  }, [selectedTransportOptMemo]);

  const showPrice4subcontracted = useMemo(() => {
    if (!transportCarrierId) return false;
    return !isInternalCarrierMemo;
  }, [transportCarrierId, isInternalCarrierMemo]);

  const selectedClientContactEmailOptions = useMemo(() => {
    if (!clientType || !selectedClient?.value) return [];

    const sourceList = clientType === "industry" ? industryClients : carrierClients;
    const fullClient = (sourceList || []).find(
      (c) => String(c.id) === String(selectedClient.value)
    );

    return extractClientEmails(fullClient || null);
  }, [clientType, selectedClient, industryClients, carrierClients]);


  const selectedSubcontractedEmailOptions = useMemo(() => {
    if (!showPrice4subcontracted || !transportCarrierId) return [];

    const selectedCarrier = (carrierClients || []).find(
      (c) => String(c.id) === String(transportCarrierId)
    );

    return extractClientEmails(selectedCarrier || null);
  }, [showPrice4subcontracted, transportCarrierId, carrierClients]);

  const multiSelectHorizontalStyles = useMemo(() => {
    const base = customSelectStyles || {};

    return {
      ...base,

      control: (provided, state) => ({
        ...(base.control ? base.control(provided, state) : provided),
        minHeight: 44,
        height: "auto",
        overflow: "hidden",
        alignItems: "center",
      }),

      valueContainer: (provided, state) => ({
        ...(base.valueContainer ? base.valueContainer(provided, state) : provided),

        // ✅ keep email chips in one single line
        flexWrap: "nowrap",

        // ✅ horizontal scroll instead of growing vertically
        overflowX: "auto",
        overflowY: "hidden",
        whiteSpace: "nowrap",

        alignItems: "center",
        paddingTop: 6,
        paddingBottom: 6,

        // ✅ leaves room for clear + dropdown icons
        paddingRight: 72,

        gap: 6,
      }),

      multiValue: (provided, state) => ({
        ...(base.multiValue ? base.multiValue(provided, state) : provided),
        flex: "0 0 auto",
        maxWidth: "none",
        margin: 0,
      }),

      multiValueLabel: (provided, state) => ({
        ...(base.multiValueLabel ? base.multiValueLabel(provided, state) : provided),
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }),

      indicatorsContainer: (provided, state) => ({
        ...(base.indicatorsContainer ? base.indicatorsContainer(provided, state) : provided),
        alignItems: "center",
        paddingTop: 0,
      }),

      menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
      }),
    };
  }, []);

  useEffect(() => {
    if (!showPrice4subcontracted) {
      setPrice4subcontracted("");
      setPrice4subcontractedError("");
      setSelectedSubcontractedEmails([]);
    }
  }, [showPrice4subcontracted]);


  // === Dimensão: limites e validador (normal vs special) ===
  // Normal maxima
  const NORMAL_MAX = { length: 13.6, height: 2.7, width: 2.5 };

  // Special maxima (allowed if specialLoad=true)
  const SPECIAL_MAX = { length: 20, height: 5, width: 4 };

  // Thresholds that make a dimension "special"
  const SPECIAL_THRESHOLD = { length: 13.61, height: 2.71, width: 2.51 };

  // ✅ SpecialLoad NO LONGER forces ALL mins to be special thresholds.
  //    It only increases MAX allowed values.
  const getDimLimits = (isSpecial) => {
    if (isSpecial) {
      return {
        length: {
          min: 0.01,
          max: SPECIAL_MAX.length,
          msg: t("errors.specialLengthMax", {
            defaultValue: `❌ Special Load length cannot exceed ${SPECIAL_MAX.length.toFixed(2)}m.`,
          }),
        },
        height: {
          min: 0.01,
          max: SPECIAL_MAX.height,
          msg: t("errors.specialHeightMax", {
            defaultValue: `❌ Special Load height cannot exceed ${SPECIAL_MAX.height.toFixed(2)}m.`,
          }),
        },
        width: {
          min: 0.01,
          max: SPECIAL_MAX.width,
          msg: t("errors.specialWidthMax", {
            defaultValue: `❌ Special Load width cannot exceed ${SPECIAL_MAX.width.toFixed(2)}m.`,
          }),
        },
      };
    }

    return {
      length: { min: 0.01, max: NORMAL_MAX.length, msg: t("errors.maxLength") },
      height: { min: 0.01, max: NORMAL_MAX.height, msg: t("errors.maxHeight") },
      width:  { min: 0.01, max: NORMAL_MAX.width,  msg: t("errors.maxWidth") },
    };
  };

  const validateDim = (key, value, isSpecial) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return "";
    }

    const limits = getDimLimits(isSpecial)[key];
    const num = Number(value);

    if (!Number.isFinite(num) || num <= 0) {
      return t("errors.fieldRequired", {
        defaultValue: "❌ Introduz um número válido superior a 0.",
      });
    }

    if (num > limits.max) return limits.msg;
    return "";
  };

  const validateWeight = (value) => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return "";
    }

    const num = Number(value);

    if (!Number.isFinite(num) || num <= 0) {
      return t("errors.weightMustBePositive", {
        defaultValue: "❌ Weight must be greater than 0.",
      });
    }

    return "";
  };

  const getPointDimensionError = (field, value) => {
    const n = Number(value);

    if (value === "" || value == null) return "";
    if (!Number.isFinite(n) || n <= 0) {
      return t("errors.fieldRequired", { defaultValue: "❌ Field is required." });
    }

    const limits = getDimLimits(specialLoad);

    if (field === "length") {
      if (n > limits.length.max) return limits.length.msg;
      return "";
    }

    if (field === "width") {
      if (n > limits.width.max) return limits.width.msg;
      return "";
    }

    if (field === "height") {
      if (n > limits.height.max) return limits.height.msg;
      return "";
    }

    return "";
  };

  const handlePointDimensionChange = (index, field, value, isPickup) => {
    const dimensionError = getPointDimensionError(field, value);
    const type = isPickup ? "pickup" : "delivery";

    const apply = (prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        [`${field}Error`]: dimensionError,
      };
      return updated;
    };

    if (isPickup) {
      setPickupPoints(apply);
    } else {
      setDeliveryPoints(apply);
    }

    setInvalidFields((prev) => {
      const next = { ...prev };

      if (dimensionError) {
        next[`${type}-${field}-${index}`] = true;
      } else {
        delete next[`${type}-${field}-${index}`];
      }

      return next;
    });
  };

  const calculatePointDimensionTotals = (points = []) => {
    return points.reduce(
      (acc, point) => {
        const pointLength = Number(point.length || 0);
        const pointWidth = Number(point.width || 0);
        const pointHeight = Number(point.height || 0);

        return {
          length: acc.length + (Number.isFinite(pointLength) ? pointLength : 0),
          width: Math.max(acc.width, Number.isFinite(pointWidth) ? pointWidth : 0),
          height: Math.max(acc.height, Number.isFinite(pointHeight) ? pointHeight : 0),
        };
      },
      { length: 0, width: 0, height: 0 }
    );
  };

  const pickupDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(pickupPoints);
  }, [pickupPoints]);

  const deliveryDimensionTotals = useMemo(() => {
    return calculatePointDimensionTotals(deliveryPoints);
  }, [deliveryPoints]);

  const pointDimensionSummary = useMemo(() => {
    const pickupLength = Number(pickupDimensionTotals.length || 0);
    const deliveryLength = Number(deliveryDimensionTotals.length || 0);

    return {
      length: pickupLength,
      pickupLength,
      deliveryLength,
      width: Math.max(pickupDimensionTotals.width, deliveryDimensionTotals.width),
      height: Math.max(pickupDimensionTotals.height, deliveryDimensionTotals.height),
      lengthMatches:
        Math.round(pickupLength * 100) === Math.round(deliveryLength * 100),
    };
  }, [pickupDimensionTotals, deliveryDimensionTotals]);

  useEffect(() => {
    const nextLength =
      pointDimensionSummary.length > 0
        ? pointDimensionSummary.length.toFixed(2)
        : "";

    const nextWidth =
      pointDimensionSummary.width > 0
        ? pointDimensionSummary.width.toFixed(2)
        : "";

    const nextHeight =
      pointDimensionSummary.height > 0
        ? pointDimensionSummary.height.toFixed(2)
        : "";

    setLength(nextLength);
    setWidth(nextWidth);
    setHeight(nextHeight);

    if (nextLength) setLengthError("");
    if (nextWidth) setWidthError("");
    if (nextHeight) setHeightError("");
  }, [
    pointDimensionSummary.length,
    pointDimensionSummary.width,
    pointDimensionSummary.height,
  ]);

  useEffect(() => {
    const fetchPlaces = async () => {
      try {
        const res = await axios.get("/places");
        const options = res.data.map((place) => ({
          value: place.id,
          label: place.placeName,
          city: place.city || "",
          country: place.country || "",
          fullData: place,
        }));
        setPlaceOptions(options);
      } catch (err) {
        console.error("❌ Failed to fetch places:", err);
      }
    };

    fetchPlaces();
  }, []);

  const PlaceTwoLine = ({ title, sub }) => (
    <div className="place-option">
      <div className="place-option__title">{title}</div>
      {sub ? <div className="place-option__subtitle">{sub}</div> : null}
    </div>
  );

  const formatPlaceOptionLabel = (option /*, meta */) => {
    const city = option.fullData?.city ?? option.city ?? "";
    const country = option.fullData?.country ?? option.country ?? "";
    const sub = [city, country].filter(Boolean).join(", ");
    return <PlaceTwoLine title={option.label} sub={sub} />;
  };

  const getPlaceValueFromPoint = (point, options) => {
    if (point.placeId) {
      return options.find((o) => o.value === point.placeId) || null;
    }
    if (point.location) {
      return {
        value: point.location,
        label: point.location,
        city: point.city || "",
        country: point.country || "",
      };
    }
    return null;
  };

  const validateFields = () => {
    const errors = {};

    const lenErr = validateDim("length", length, specialLoad);
    const widErr = validateDim("width", width, specialLoad);
    const heiErr = validateDim("height", height, specialLoad);
    const weiErr = validateWeight(weight);

    setLengthError(lenErr);
    setWidthError(widErr);
    setHeightError(heiErr);
    setWeightError(weiErr);

    if (lenErr) errors.length = lenErr;
    if (widErr) errors.width = widErr;
    if (heiErr) errors.height = heiErr;
    if (weiErr) errors.weight = weiErr;

    const hasAnyDimension =
      String(length || "").trim() !== "" ||
      String(height || "").trim() !== "" ||
      String(width || "").trim() !== "";

    if (specialLoad && hasAnyDimension) {
      const L = Number(length);
      const H = Number(height);
      const W = Number(width);

      const hasSpecialDim =
        (Number.isFinite(L) && L >= SPECIAL_THRESHOLD.length) ||
        (Number.isFinite(H) && H >= SPECIAL_THRESHOLD.height) ||
        (Number.isFinite(W) && W >= SPECIAL_THRESHOLD.width);

      if (!hasSpecialDim) {
        errors.specialLoad = t("errors.specialNeedsOneDimension", {
          defaultValue:
            "⚠️ This load is marked as Special Load, but no dimension is special. " +
            `Increase at least one: Length (≥ ${SPECIAL_THRESHOLD.length}m), ` +
            `Height (≥ ${SPECIAL_THRESHOLD.height}m) or Width (≥ ${SPECIAL_THRESHOLD.width}m).`,
        });
      }
    }

    const merchandiseValue =
      typeof merchandise === "string" ? merchandise.trim() : "";

    let localMerchandiseErr = "";

    if (!merchandiseValue) {
      localMerchandiseErr = t("errors.merchandiseRequired", {
        defaultValue: "❌ Merchandise is required.",
      });
    } else if (merchandiseValue.length > 5000) {
      localMerchandiseErr = t("errors.merchandiseTooLong", {
        defaultValue: "❌ Merchandise is too long (max 5000 characters).",
      });
    }

    setMerchandiseError(localMerchandiseErr);

    if (localMerchandiseErr) {
      errors.merchandise = localMerchandiseErr;
    }

    if (price != null && String(price).trim() !== "") {
      const n = Number(price);

      if (!Number.isFinite(n) || n < 0) {
        const priceValidationError = t("errors.invalidPrice");
        setPriceError(priceValidationError);
        errors.price = priceValidationError;
      } else {
        setPriceError("");
      }
    } else {
      setPriceError("");
    }

    if (
      showPrice4subcontracted &&
      price4subcontracted != null &&
      String(price4subcontracted).trim() !== ""
    ) {
      const n = Number(price4subcontracted);

      if (!Number.isFinite(n) || n < 0) {
        const subcontractedPriceValidationError = t("errors.invalidPrice");
        setPrice4subcontractedError(subcontractedPriceValidationError);
        errors.price4subcontracted = subcontractedPriceValidationError;
      } else {
        setPrice4subcontractedError("");
      }
    } else {
      setPrice4subcontractedError("");
    }

    if (
      String(trailerType || "").trim().toUpperCase() === "OTHER" &&
      !String(trailerTypeOther || "").trim()
    ) {
      errors.trailerTypeOther = t("errors.trailerTypeOtherRequired", {
        defaultValue: "❌ Please specify the trailer type.",
      });
    }

    setInvalidFields(errors);
    return errors;
  };

  const validateAllDeliveryFields = () => {
    const deliveryErrors = checkDeliveryFieldErrors(deliveryPoints);
    const hasErrors = Object.keys(deliveryErrors).length > 0;

    if (hasErrors) {
      setInvalidFields((prev) => ({ ...prev, ...deliveryErrors }));
      setDeliveryFieldValidationError(true);
      return false;
    }

    setDeliveryFieldValidationError(false);
    return true;
  };

  function sanitizedPoints(points) {
    return points.map((p) => {
      const handlingEquipment = p.handlingEquipment
        ? String(p.handlingEquipment).trim().toUpperCase()
        : null;

      const handlingEquipmentOther =
        handlingEquipment === "OTHER"
          ? (p.handlingEquipmentOther?.trim() ? p.handlingEquipmentOther.trim() : null)
          : null;

      return {
        placeId: p.placeId ?? null,
        location: p.location?.trim() || "",
        date: p.date || null,
        hour: p.hour || null,

        // 🆕 point-level dimensions
        length: p.length === "" || p.length == null ? null : Number(p.length),
        width: p.width === "" || p.width == null ? null : Number(p.width),
        height: p.height === "" || p.height == null ? null : Number(p.height),

        address: p.address?.trim() || "",
        postalCode: p.postalCode?.trim() || "",
        city: p.city?.trim() || "",
        country: p.country?.trim() || "",
        gMapsLink: p.gMapsLink?.trim() || "",
        coordinates: p.coordinates?.trim() || "",
        contactPhone: p.contactPhone?.trim() || "",

        // 🆕 NEW (optional)
        reference: p.reference?.trim() || null,

        // 🆕 NEW
        loadingAccess: p.loadingAccess ? String(p.loadingAccess).trim().toUpperCase() : null,

        // 🆕 NEW (per point)
        handlingEquipment,
        handlingEquipmentOther,
      };
    });
  }

  const handleCreate = async () => {
    setDidSubmit(true);
    setError("");
    setClientTypeError(false);
    setSelectedClientError(false);
    setCarrierError(false);

    const generalErrors = validateFields();
    const pickupErrors = checkPickupFieldErrors(pickupPoints);
    const deliveryErrors = checkDeliveryFieldErrors(deliveryPoints);

    const hasInvalidPickupHourFormat = pickupPoints.some(
      (p) => p.hour && !/^([01]\d|2[0-3]):([0-5]\d)$/.test(p.hour)
    );

    const hasInvalidDeliveryHourFormat = deliveryPoints.some(
      (p) => p.hour && !/^([01]\d|2[0-3]):([0-5]\d)$/.test(p.hour)
    );

    const hasPickupErrors = Object.keys(pickupErrors).length > 0;
    const hasDeliveryErrors = Object.keys(deliveryErrors).length > 0;

    setPickupFieldValidationError(hasPickupErrors);
    setDeliveryFieldValidationError(hasDeliveryErrors);
    setGlobalHourFormatError(
      hasInvalidPickupHourFormat || hasInvalidDeliveryHourFormat
    );

    const combinedErrors = {
      ...generalErrors,
      ...pickupErrors,
      ...deliveryErrors,
    };

    setInvalidFields(combinedErrors);

    if (
      Object.keys(combinedErrors).length > 0 ||
      hasInvalidPickupHourFormat ||
      hasInvalidDeliveryHourFormat
    ) {
      return;
    }

    try {
      setLoading(true);

      let parsedPrice = null;
      if (price != null && String(price).trim() !== "") {
        const n = Number(price);
        parsedPrice = Number.isFinite(n) ? Number(n.toFixed(2)) : null;
      }

      let parsedPrice4subcontracted = null;
      if (
        showPrice4subcontracted &&
        price4subcontracted != null &&
        String(price4subcontracted).trim() !== ""
      ) {
        const n = Number(price4subcontracted);
        parsedPrice4subcontracted = Number.isFinite(n)
          ? Number(n.toFixed(2))
          : null;
      }

      const tt = trailerType ? String(trailerType).trim().toUpperCase() : null;
      const tto =
        tt === "OTHER" ? (String(trailerTypeOther || "").trim() || null) : null;

      const selectedTransportOpt = availableCarriers.find(
        (c) => String(c.value) === String(transportCarrierId)
      );

      const isInternalCarrier =
        selectedTransportOpt?.isInternal ||
        String(selectedTransportOpt?.value) === "__internal__";

      const payload = {
        length:
          String(length || "").trim() !== "" ? Number(length) : null,
        height:
          String(height || "").trim() !== "" ? Number(height) : null,
        width:
          String(width || "").trim() !== "" ? Number(width) : null,
        weight:
          String(weight || "").trim() !== "" ? Number(weight) : null,
        price: parsedPrice,
        price4subcontracted: parsedPrice4subcontracted,
        loadBearer,
        specialLoad,
        trailerType: tt,
        trailerTypeOther: tto,
        clientType: clientType || null,
        industryClientId:
          clientType === "industry" ? selectedClient?.value ?? null : null,
        carrierClientId:
          clientType === "carrier" ? selectedClient?.value ?? null : null,
        clientContactEmails: selectedClientContactEmails.map((opt) => opt.value),
        subcontractedEmails:
          !transportCarrierId || isInternalCarrier
            ? []
            : selectedSubcontractedEmails.map((opt) => opt.value),
        carrierId:
          !transportCarrierId || isInternalCarrier
            ? null
            : Number(transportCarrierId),
        carrierName:
          !transportCarrierId
            ? null
            : isInternalCarrier
            ? (
                companyName ||
                t("general.internalCarrier", { defaultValue: "Internal carrier" })
              )
            : selectedTransportOpt?.label || null,
        pickupPoints: sanitizedPoints(pickupPoints),
        deliveryPoints: sanitizedPoints(deliveryPoints),
        notes4driver:
          typeof notes4driver === "string" &&
          notes4driver.trim().length > 0
            ? notes4driver.trim().slice(0, 10000)
            : null,
        merchandise: merchandise.trim().slice(0, 5000),
      };

      console.log("📦 Payload to backend (pendentload):", payload);

      const response = await axios.post("/pendentloads/create", payload);

      if (response.status === 201) {
        const createdId =
          response.data?.pendentLoad?.id ??
          response.data?.pendingLoad?.id ??
          response.data?.load?.id ??
          response.data?.id ??
          null;

        setCreatedPendentLoadId(createdId);
        setSuccessPopupOpen(true);
      } else {
        setError(t("errors.failedCreate"));
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        "❌ Error while creating pendent load:",
        status,
        data
      );
      setError(
        typeof data === "string"
          ? `❌ ${data}`
          : data?.error
          ? `❌ ${data.error}`
          : t("errors.serverError")
      );
    } finally {
      setLoading(false);
    }
  };

  const getWeekdayLabel = (dayKey) => {
    if (!dayKey) return "—";
    const lower = dayKey.toLowerCase();
    return t(`weekday.${lower}`, dayKey);
  };

  return (
    <div className="create-pendent-load-container">
      <div
        className="create-pendent-load-back-arrow"
        onClick={() => navigate("/loads?view=pendent")}
      >
        ← {t("back")}
      </div>

      {successPopupOpen && (
        <div
          className="create-pendent-load-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-pendent-load-success-title"
        >
          <div className="create-pendent-load-popupModal">
            <div className="create-pendent-load-popupIconWrap">
              <FaCheckCircle className="create-pendent-load-popupIcon" />
            </div>

            <h2
              id="create-pendent-load-success-title"
              className="create-pendent-load-popupTitle"
            >
              {t("popup.created_title")}
            </h2>

            <p className="create-pendent-load-popupMessage">
              {createdPendentLoadId
                ? t("popup.created_message", { id: createdPendentLoadId })
                : t("popup.created_message_no_id")}
            </p>

            <div className="create-pendent-load-popupActions">
              <button
                type="button"
                className="create-pendent-load-popupPrimaryBtn"
                onClick={handleCloseSuccessPopup}
              >
                {t("popup.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="create-pendent-load-title">{t("title")}</h2>

      <div
        className="create-pendent-load-form"
        onFocusCapture={(e) => {
          const target = e.target;
          if (!target) return;

          const currentKey = Object.keys(inputRefs.current).find(
            (key) => inputRefs.current[key] === target
          );

          closeAllNativePickers(currentKey);
        }}
      >

        {/* Linha superior com switches */}
        <div className="create-pendent-load-row toggles-row">
          <div className="create-pendent-load-field-group toggle-field-group">
            <label className="create-pendent-load-label">
              {t("toggles.loadBearer")}
            </label>
            <label className="load-switch">
              <input
                type="checkbox"
                checked={loadBearer}
                onChange={() => {
                  setLoadBearer(!loadBearer);
                  if (!loadBearer) setSpecialLoad(false);
                }}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="create-pendent-load-field-group toggle-field-group">
            <label className="create-pendent-load-label">
              {t("toggles.specialLoad")}
            </label>
            <label className="load-switch">
              <input
                type="checkbox"
                checked={specialLoad}
                onChange={() => {
                  setSpecialLoad(!specialLoad);
                  if (!specialLoad) setLoadBearer(false);
                }}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>

        {/* Três colunas */}
        <div className="create-pendent-load-columns">
          {/* LOADING POINTS */}
          <div className="create-pendent-load-column">
            <h3 className="create-pendent-load-column-title">
              {t("sections.loadingPoints")}
            </h3>
            {pickupPoints.map((point, index) => (
              <div
                key={index}
                className="create-pendent-load-point-group"
              >
                {/* Label + Delete */}
                <div className="create-pendent-load-label-row">
                  <label className="create-pendent-load-points-label">
                    {t("fields.pickUpPlace")}
                  </label>
                  {pickupPoints.length > 1 && (
                    <button
                      type="button"
                      className="create-pendent-load-delete-btn"
                      onClick={() => removePickUpPlace(index)}
                      title={t("place.removePickUp")}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Location Dropdown */}
                <div className="select-wrapper-left-icon">
                  <CreatableSelect
                    isClearable
                    isMulti={false}
                    options={placeOptions}
                    placeholder={t("place.selectOrType")}
                    value={getPlaceValueFromPoint(point, placeOptions)}
                    formatOptionLabel={formatPlaceOptionLabel}
                    onChange={(selectedOption) => {
                      const updated = [...pickupPoints];
                      const updatedErrors = { ...invalidFields };

                      let newPoint = { ...updated[index] };

                      if (selectedOption?.__isNew__) {
                        newPoint = {
                          ...newPoint,
                          placeId: null,
                          location: selectedOption.label,
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          workingHours: null,
                        };
                      } else if (selectedOption?.fullData) {
                        const p = selectedOption.fullData;
                        newPoint = {
                          ...newPoint,
                          placeId: p.id,
                          location: p.placeName,
                          address: p.address,
                          postalCode: p.postalCode,
                          city: p.city,
                          country: p.country,
                          gMapsLink: p.gMapsLink,
                          coordinates: p.coordinates,
                          contactPhone: p.contactPhone,
                          workingHours: p.workingHours ?? null,
                        };
                      } else {
                        newPoint = {
                          ...newPoint,
                          placeId: null,
                          location: selectedOption?.label || "",
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          workingHours: null,
                        };
                      }

                      const fieldsToCheck = [
                        newPoint.address,
                        newPoint.postalCode,
                        newPoint.city,
                        newPoint.country,
                        newPoint.gMapsLink,
                        newPoint.coordinates,
                        newPoint.contactPhone,
                      ];
                      newPoint.showMoreFields = fieldsToCheck.some(
                        (v) => !v?.trim()
                      );

                      updated[index] = newPoint;
                      setPickupPoints(updated);

                      const fields = [
                        "location",
                        "address",
                        "postalCode",
                        "city",
                        "country",
                        "gMapsLink",
                        "coordinates",
                        "contactPhone",
                      ];
                      fields.forEach(
                        (field) =>
                          delete updatedErrors[`pickup-${field}-${index}`]
                      );
                      setInvalidFields(updatedErrors);

                      const allFilled = updated.every(
                        (p) =>
                          p.location.trim() !== "" &&
                          p.date !== "" &&
                          p.hour !== ""
                      );
                      if (allFilled) setPickupFieldValidationError(false);
                    }}
                    styles={customSelectStyles}
                    classNamePrefix={
                      invalidFields[`pickup-location-${index}`]
                        ? "select-error"
                        : "select"
                    }
                  />
                  {invalidFields[`pickup-location-${index}`] && (
                    <p className="dropdown-field-error">
                      {t("errors.locationRequired")}
                    </p>
                  )}
                </div>

                {/* Date + Hour */}
                <div className="create-pendent-load-date-hour-row">
                  <div className="custom-input-wrapper">
                    <input
                      type="date"
                      value={point.date}
                      className={`create-pendent-load-input ${
                        invalidFields[`pickup-date-${index}`]
                          ? "input-error"
                          : ""
                      }`}
                      onChange={(e) => {
                        const updated = [...pickupPoints];
                        updated[index].date = e.target.value;
                        setPickupPoints(updated);
                        setPickupFieldValidationError(
                          checkPickupFieldErrors(updated)
                        );
                        clearFieldErrors(index, "pickup", ["date"]);
                        e.target.blur();
                      }}
                      ref={(el) =>
                        (inputRefs.current[`pickup-date-${index}`] = el)
                      }
                    />
                    <FaCalendarAlt
                      className="input-icon"
                      onClick={() => {
                        closeAllNativePickers(`pickup-date-${index}`);
                        inputRefs.current[`pickup-date-${index}`]?.focus();
                        inputRefs.current[`pickup-date-${index}`]?.showPicker?.();
                      }}
                    />
                  </div>

                  <div className="custom-input-wrapper">
                    <input
                      type="text"
                      placeholder="HH:MM"
                      value={point.hour || ""}
                      className={`create-pendent-load-input ${
                        invalidFields[`pickup-hour-${index}`] || point.hourError
                          ? "input-error"
                          : ""
                      }`}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);

                        let formatted = raw;
                        if (raw.length >= 3) {
                          formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                        }

                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        const isValid = regex.test(formatted);

                        const updated = [...pickupPoints];
                        updated[index] = {
                          ...updated[index],
                          hour: formatted,
                          hourError: formatted.length === 5 && !isValid,
                        };

                        setPickupPoints(updated);
                        setGlobalHourFormatError(formatted.length === 5 && !isValid);

                        if (didSubmit) {
                          setPickupFieldValidationError(checkPickupFieldErrors(updated));
                        }

                        setInvalidFields((prev) => ({
                          ...prev,
                          [`pickup-hour-${index}`]: !formatted.trim(),
                        }));

                        clearFieldErrors(index, "pickup", ["hour"]);
                      }}
                      ref={(el) =>
                        (inputRefs.current[`pickup-hour-${index}`] = el)
                      }
                    />
                    <FaClock
                      className="input-icon"
                      onClick={() => {
                        inputRefs.current[`pickup-hour-${index}`]?.focus();
                      }}
                    />
                  </div>
                </div>
                {invalidFields[`pickup-date-${index}`] && (
                  <p className="data-error-text">
                    {t("errors.dateRequired")}
                  </p>
                )}
                {invalidFields[`pickup-hour-${index}`] && (
                  <p className="data-error-text">
                    {t("errors.hourRequired")}
                  </p>
                )}

                <div className="create-pendent-load-point-dimensions-block">
                  <label className="create-pendent-load-label create-pendent-load-point-dimensions-title">
                    {t("fields.pointDimensions", { defaultValue: "Dimensões do ponto" })}*
                  </label>

                  <div className="create-pendent-load-point-dimensions-row">
                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointLengthShort", { defaultValue: "Comp." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.length || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "length", e.target.value, true)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`pickup-length-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.lengthError && (
                        <p className="create-pendent-load-error-text">
                          {point.lengthError}
                        </p>
                      )}
                    </div>

                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointWidthShort", { defaultValue: "Larg." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.width || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "width", e.target.value, true)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`pickup-width-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.widthError && (
                        <p className="create-pendent-load-error-text">
                          {point.widthError}
                        </p>
                      )}
                    </div>

                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointHeightShort", { defaultValue: "Alt." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.height || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "height", e.target.value, true)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`pickup-height-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.heightError && (
                        <p className="create-pendent-load-error-text">
                          {point.heightError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 🆕 Loading Access (below date/hour) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: -5 }}>
                  <label className="create-pendent-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="create-pendent-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) =>
                            o.value ===
                            String(point.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, true)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={"select"}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                {/* 🆕 Handling Equipment (per point) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: 2 }}>
                  <label className="create-pendent-load-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="create-pendent-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, true)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        invalidFields[`pickup-handlingEquipment-${index}`] ? "select-error" : "select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <>
                      <label className="create-pendent-load-label" style={{ marginTop: 6 }}>
                        {t("handlingEquipment.otherLabel", { defaultValue: "Outro (especificar)" })}
                      </label>

                      <input
                        type="text"
                        value={point.handlingEquipmentOther || ""}
                        placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Please specify..." })}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index] = { ...updated[index], handlingEquipmentOther: e.target.value };
                          setPickupPoints(updated);

                          // limpa erro ao escrever
                          setInvalidFields((prev) => {
                            const next = { ...prev };
                            delete next[`pickup-handlingEquipmentOther-${index}`];
                            return next;
                          });
                        }}
                        className={`create-pendent-load-input ${
                          invalidFields[`pickup-handlingEquipmentOther-${index}`] ? "input-error" : ""
                        }`}
                      />
                    </>
                  )}

                  {invalidFields[`pickup-handlingEquipmentOther-${index}`] && (
                    <p className="data-error-text">
                      {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the handling equipment." })}
                    </p>
                  )}
                </div>

                {/* 🆕 Reference (optional) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: -2 }}>
                  <label className="create-pendent-load-label">
                    {t("fields.reference", { defaultValue: "Reference" })}
                  </label>

                  <input
                    type="text"
                    value={point.reference || ""}
                    placeholder={t("placeholders.reference", {
                      defaultValue: "Optional reference…",
                    })}
                    onChange={(e) => {
                      const updated = [...pickupPoints];
                      updated[index] = { ...updated[index], reference: e.target.value };
                      setPickupPoints(updated);
                    }}
                    className="create-pendent-load-input"
                  />
                </div>

                {/* Extra Fields */}
                {point.showMoreFields ? (
                  <>
                    <div className="create-pendent-load-more-fields">
                      {/* Read-only working hours badge */}
                      {(() => {
                        const dayKey = weekdayKeyFromISO(point.date);
                        const whObj = parseWorkingHours(
                          point.workingHours
                        );
                        const text = formatIntervalsForDay(
                          whObj,
                          dayKey
                        );
                        return (
                          <div className="pendent-load-details-working-hours-badge">
                            <span className="pendent-load-details-working-hours-day">
                              {getWeekdayLabel(dayKey)}
                            </span>
                            <span className="pendent-load-details-working-hours-text">
                              {text ?? t("workingHours.noInfo")}
                            </span>
                          </div>
                        );
                      })()}
                      {/* Linha 1: Address */}
                      <input
                        type="text"
                        placeholder={t("fields.address")}
                        value={point.address || ""}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index].address = e.target.value;
                          setPickupPoints(updated);
                          setPickupFieldValidationError(
                            checkPickupFieldErrors(updated)
                          );
                          clearFieldErrors(index, "pickup", ["address"]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`pickup-address-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 2: Postal Code, City, Country (horizontal) */}
                      <div className="create-pendent-load-row-tight">
                        <input
                          type="text"
                          placeholder={t("fields.postalCode")}
                          value={point.postalCode || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].postalCode = e.target.value;
                            setPickupPoints(updated);
                            setPickupFieldValidationError(
                              checkPickupFieldErrors(updated)
                            );
                            clearFieldErrors(index, "pickup", [
                              "postalCode",
                            ]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`pickup-postalCode-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />
                        <input
                          type="text"
                          placeholder={t("fields.city")}
                          value={point.city || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].city = e.target.value;
                            setPickupPoints(updated);
                            setPickupFieldValidationError(
                              checkPickupFieldErrors(updated)
                            );
                            clearFieldErrors(index, "pickup", ["city"]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`pickup-city-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />
                        <input
                          type="text"
                          placeholder={t("fields.country")}
                          value={point.country || ""}
                          onChange={(e) => {
                            const updated = [...pickupPoints];
                            updated[index].country = e.target.value;
                            setPickupPoints(updated);
                            setPickupFieldValidationError(
                              checkPickupFieldErrors(updated)
                            );
                            clearFieldErrors(index, "pickup", ["country"]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`pickup-country-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />
                      </div>

                      {/* Linha 3: Contact Phone */}
                      <input
                        type="text"
                        placeholder={t("fields.contactPhone")}
                        value={point.contactPhone || ""}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index].contactPhone = e.target.value;
                          setPickupPoints(updated);
                          setPickupFieldValidationError(
                            checkPickupFieldErrors(updated)
                          );
                          clearFieldErrors(index, "pickup", [
                            "contactPhone",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`pickup-contactPhone-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 4: Google Maps Link */}
                      <input
                        type="text"
                        placeholder={t("fields.gMapsLink")}
                        value={point.gMapsLink || ""}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index].gMapsLink = e.target.value;
                          setPickupPoints(updated);
                          setPickupFieldValidationError(
                            checkPickupFieldErrors(updated)
                          );
                          clearFieldErrors(index, "pickup", ["gMapsLink"]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`pickup-gMapsLink-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 5: Coordinates */}
                      <input
                        type="text"
                        placeholder={t("fields.coordinates")}
                        value={point.coordinates || ""}
                        onChange={(e) => {
                          const updated = [...pickupPoints];
                          updated[index].coordinates = e.target.value;
                          setPickupPoints(updated);
                          setPickupFieldValidationError(
                            checkPickupFieldErrors(updated)
                          );
                          clearFieldErrors(index, "pickup", [
                            "coordinates",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`pickup-coordinates-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="create-pendent-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="create-pendent-load-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...pickupPoints];
                          updated[index].showMoreFields = false;
                          setPickupPoints(updated);
                        }}
                      >
                        {t("buttons.hideFields")}{" "}
                        <FiChevronUp className="create-pendent-load-toggle-arrow" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="create-pendent-load-toggle-fields-wrapper">
                    <button
                      type="button"
                      className="create-pendent-load-toggle-fields-btn"
                      onClick={() => {
                        const updated = [...pickupPoints];
                        updated[index].showMoreFields = true;
                        setPickupPoints(updated);
                      }}
                    >
                      {t("buttons.moreFields")}{" "}
                      <FiChevronDown className="create-pendent-load-toggle-arrow" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {pickupPoints.length < 7 ? (
              <button
                className="create-pendent-load-add-button"
                onClick={() =>
                  setPickupPoints([
                    ...pickupPoints,
                    {
                      location: "",
                      date: "",
                      hour: "",
                      hourError: false,

                      // 🆕 point-level dimensions
                      length: "",
                      width: "",
                      height: "",
                      lengthError: "",
                      widthError: "",
                      heightError: "",

                      placeId: null,
                      address: "",
                      postalCode: "",
                      city: "",
                      country: "",
                      gMapsLink: "",
                      coordinates: "",
                      contactPhone: "",
                      showMoreFields: false,
                      workingHours: null,
                      loadingAccess: null,
                      handlingEquipment: null,
                      handlingEquipmentOther: "",
                      reference: "",
                    },
                  ])
                }
              >
                {t("buttons.addPickUp")}
              </button>
            ) : (
              <p className="create-pendent-load-warning-text">
                {t("warnings.maxPickups")}
              </p>
            )}
          </div>

          {/* Column 2: Merchandise & General Information */}
          <div className="create-pendent-load-column">
            <h3 className="create-pendent-load-column-title">
              {t("sections.merchandise")}
            </h3>

            <div className="create-pendent-load-merchandise-inline-row">
              {/* Weight */}
              <div className="create-pendent-load-field-group create-pendent-load-weight-field">
                <label className="create-pendent-load-label">
                  {t("fields.weight", { defaultValue: "Peso (t)*" })}
                </label>

                <input
                  type="number"
                  step="0.001"
                  value={weight}
                  onChange={(e) => {
                    const v = e.target.value;
                    setWeight(v);
                    setWeightError(validateWeight(v));
                  }}
                  className={`create-pendent-load-input ${
                    weightError ? "input-error" : ""
                  }`}
                  placeholder="0"
                />

                {weightError && (
                  <p className="create-pendent-load-error-text">
                    {weightError}
                  </p>
                )}
              </div>

              {/* Merchandise */}
              <div className="create-pendent-load-field-group create-pendent-load-merchandise-field">
                <label className="create-pendent-load-label">
                  {t("fields.merchandise", { defaultValue: "Mercadoria*" })}
                </label>

                <input
                  type="text"
                  placeholder={t("fields.merchandisePlaceholder", {
                    defaultValue: "ex.: Paletes, Alimentar, Mobiliário...",
                  })}
                  value={merchandise}
                  onChange={(e) => {
                    setMerchandise(e.target.value);
                    if (merchandiseError) setMerchandiseError("");
                  }}
                  className={`create-pendent-load-input ${
                    merchandiseError ? "input-error" : ""
                  }`}
                />

                {merchandiseError && (
                  <p className="create-pendent-load-error-text">
                    {merchandiseError}
                  </p>
                )}
              </div>
            </div>

            {invalidFields?.specialLoad && (
              <p className="create-pendent-load-error-text create-pendent-load-special-dim-error">
                {invalidFields.specialLoad}
              </p>
            )}

            <div className="create-pendent-load-load-dimensions-summary">
              <label className="create-pendent-load-label create-pendent-load-load-dimensions-title">
                {t("fields.loadDimensions", {
                  defaultValue: "Dimensões totais da carga",
                })}
              </label>

              <div className="create-pendent-load-load-dimensions-row">
                <div className="create-pendent-load-load-dimension-field">
                  <label>
                    {t("fields.length", { defaultValue: "Comprimento (m)*" })}
                  </label>
                  <input
                    type="text"
                    value={length}
                    readOnly
                    className={`create-pendent-load-input create-pendent-load-auto-calculated-input ${
                      lengthError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div className="create-pendent-load-load-dimension-field">
                  <label>
                    {t("fields.width", { defaultValue: "Largura (m)*" })}
                  </label>
                  <input
                    type="text"
                    value={width}
                    readOnly
                    className={`create-pendent-load-input create-pendent-load-auto-calculated-input ${
                      widthError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>

                <div className="create-pendent-load-load-dimension-field">
                  <label>
                    {t("fields.height", { defaultValue: "Altura (m)*" })}
                  </label>
                  <input
                    type="text"
                    value={height}
                    readOnly
                    className={`create-pendent-load-input create-pendent-load-auto-calculated-input ${
                      heightError ? "input-error" : ""
                    }`}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {!pointDimensionSummary.lengthMatches && (
                <p className="create-pendent-load-dimension-warning-text">
                  {t("errors.pickupDeliveryLengthMismatch", {
                    defaultValue:
                      "⚠️ A soma dos comprimentos das cargas e descargas ainda não coincide.",
                  })}
                </p>
              )}

              <div className="create-pendent-load-dimension-helper-text">
                {t("fields.loadDimensionsHelper", {
                  defaultValue:
                    "Comprimento calculado pela soma dos pontos. Largura e altura usam o maior valor registado.",
                })}
              </div>
            </div>

            {/* Trailer Type */}
            <div className="create-pendent-load-field-group create-pendent-load-trailer-type-field">
              <label className="create-pendent-load-label">
                {t("trailerType.label", { defaultValue: "Tipo de semirreboque*" })}
              </label>

              <Select
                className="create-pendent-load-fullwidth-select"
                options={trailerTypeOptions}
                value={
                  trailerTypeOptions.find(
                    (o) =>
                      o.value ===
                      String(trailerType || "").trim().toUpperCase()
                  ) || null
                }
                onChange={(opt) => {
                  const v = opt?.value
                    ? String(opt.value).trim().toUpperCase()
                    : null;

                  setTrailerType(v);

                  if (v !== "OTHER") setTrailerTypeOther("");

                  setInvalidFields((prev) => {
                    const next = { ...prev };
                    delete next.trailerType;
                    delete next.trailerTypeOther;
                    return next;
                  });
                }}
                isClearable
                placeholder={t("trailerType.placeholder", {
                  defaultValue: "Seleciona o tipo de reboque...",
                })}
                styles={customSelectStyles}
                classNamePrefix={
                  invalidFields.trailerType ? "select-error" : "select"
                }
              />

              {invalidFields.trailerType && (
                <p className="create-pendent-load-dropdown-field-error">
                  {invalidFields.trailerType}
                </p>
              )}
            </div>

            {String(trailerType || "").trim().toUpperCase() === "OTHER" && (
              <div className="create-pendent-load-field-group" style={{ marginTop: -4 }}>
                <label className="create-pendent-load-label">
                  {t("trailerType.otherLabel", {
                    defaultValue: "Outro (especificar)",
                  })}
                </label>

                <input
                  type="text"
                  value={trailerTypeOther || ""}
                  placeholder={t("trailerType.otherPlaceholder", {
                    defaultValue: "Especifica o tipo de reboque...",
                  })}
                  onChange={(e) => {
                    setTrailerTypeOther(e.target.value);

                    setInvalidFields((prev) => {
                      const next = { ...prev };
                      delete next.trailerTypeOther;
                      return next;
                    });
                  }}
                  className={`create-pendent-load-input ${
                    invalidFields.trailerTypeOther ? "input-error" : ""
                  }`}
                />

                {invalidFields.trailerTypeOther && (
                  <p className="create-pendent-load-error-text">
                    {invalidFields.trailerTypeOther}
                  </p>
                )}
              </div>
            )}

            <h3 className="create-pendent-load-general-info-title">
              {t("sections.generalInformation")}
            </h3>

            {/* Price */}
            <div className="create-pendent-load-field-group">
              <label className="create-pendent-load-label">
                {t("fields.price")}
              </label>

              <div
                className={`create-pendent-load-price-wrapper ${
                  priceError ? "input-error" : ""
                }`}
              >
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder={t("placeholders.priceExample", {
                    defaultValue: "ex.: 850.00",
                  })}
                  value={price}
                  onChange={(e) => {
                    const v = normalizeMoney(e.target.value);

                    if (
                      /^\d{0,9}(\.\d{0,2})?$/.test(v) ||
                      v === "" ||
                      v === "."
                    ) {
                      setPrice(v === "." ? "0." : v);
                      if (priceError) setPriceError("");
                    }
                  }}
                  onBlur={() => {
                    if (price == null || String(price).trim() === "") {
                      setPriceError("");
                      return;
                    }

                    const n = Number(price);

                    if (!Number.isFinite(n) || n < 0) {
                      setPriceError(t("errors.invalidPrice"));
                    } else {
                      setPrice(n.toFixed(2));
                      setPriceError("");
                    }
                  }}
                  className="create-pendent-load-input create-pendent-load-price-input"
                />

                <div className="create-pendent-load-price-currency">
                  <Select
                    options={[
                      { value: "EUR", label: "EUR" },
                      { value: "USD", label: "USD" },
                      { value: "GBP", label: "GBP" },
                    ]}
                    value={{ value: priceCurrency, label: priceCurrency }}
                    onChange={(opt) => setPriceCurrency(opt?.value || "EUR")}
                    isSearchable={false}
                    styles={customSelectStyles}
                    classNamePrefix="select"
                  />
                </div>
              </div>

              {priceError && (
                <p className="create-pendent-load-error-text" style={{ marginTop: 4 }}>
                  {priceError}
                </p>
              )}

              {price && !priceError && (
                <div className="create-pendent-load-price-preview">
                  {formatPrice(price, priceCurrency)}
                </div>
              )}
            </div>

            {showPrice4subcontracted && (
              <div className="create-pendent-load-field-group">
                <label className="create-pendent-load-label">
                  {t("fields.price4subcontracted", {
                    defaultValue: "Preço para Subcontratado",
                  })}
                </label>

                <div
                  className={`create-pendent-load-price-wrapper ${
                    price4subcontractedError ? "input-error" : ""
                  }`}
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={t("placeholders.price4subcontractedExample", {
                      defaultValue: "ex.: 850.00",
                    })}
                    value={price4subcontracted}
                    onChange={(e) => {
                      const v = normalizeMoney(e.target.value);

                      if (
                        /^\d{0,9}(\.\d{0,2})?$/.test(v) ||
                        v === "" ||
                        v === "."
                      ) {
                        setPrice4subcontracted(v === "." ? "0." : v);
                        if (price4subcontractedError) {
                          setPrice4subcontractedError("");
                        }
                      }
                    }}
                    onBlur={() => {
                      if (
                        price4subcontracted == null ||
                        String(price4subcontracted).trim() === ""
                      ) {
                        setPrice4subcontractedError("");
                        return;
                      }

                      const n = Number(price4subcontracted);

                      if (!Number.isFinite(n) || n < 0) {
                        setPrice4subcontractedError(t("errors.invalidPrice"));
                      } else {
                        setPrice4subcontracted(n.toFixed(2));
                        setPrice4subcontractedError("");
                      }
                    }}
                    className="create-pendent-load-input create-pendent-load-price-input"
                  />

                  <div className="create-pendent-load-price-currency">
                    <Select
                      options={[
                        { value: "EUR", label: "EUR" },
                        { value: "USD", label: "USD" },
                        { value: "GBP", label: "GBP" },
                      ]}
                      value={{ value: priceCurrency, label: priceCurrency }}
                      isDisabled
                      isSearchable={false}
                      styles={customSelectStyles}
                      classNamePrefix="select"
                    />
                  </div>
                </div>

                {price4subcontractedError && (
                  <p className="create-pendent-load-error-text" style={{ marginTop: 4 }}>
                    {price4subcontractedError}
                  </p>
                )}

                {price4subcontracted && !price4subcontractedError && (
                  <div className="create-pendent-load-price-preview">
                    {formatPrice(price4subcontracted, priceCurrency)}
                  </div>
                )}
              </div>
            )}

            <div className="create-pendent-load-field-group create-pendent-load-client-section">
              <label className="create-pendent-load-label">
                {t("fields.clientType")}
              </label>

              <Select
                className="create-pendent-load-fullwidth-select"
                value={
                  clientType
                    ? {
                        label:
                          clientType === "industry"
                            ? t("fields.industry")
                            : t("fields.carrier"),
                        value: clientType,
                      }
                    : null
                }
                onChange={(selected) => {
                  const internalOpt = availableCarriers.find(
                    (c) => c?.isInternal || String(c?.value) === "__internal__"
                  );

                  if (selected) {
                    setClientType(selected.value);
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                    setClientTypeError(false);

                    if (internalOpt) {
                      setTransportCarrierId(String(internalOpt.value));
                      setCarrierError(false);
                    }
                  } else {
                    setClientType("");
                    setSelectedClient(null);
                    setSelectedClientContactEmails([]);
                    setClientTypeError(true);

                    if (internalOpt) {
                      setTransportCarrierId(String(internalOpt.value));
                      setCarrierError(false);
                    }
                  }
                }}
                options={[
                  { value: "industry", label: t("fields.industry") },
                  { value: "carrier", label: t("fields.carrier") },
                ]}
                styles={customSelectStyles}
                placeholder={t("placeholders.selectClientType")}
                isClearable
                isSearchable={false}
                classNamePrefix={clientTypeError ? "select-error" : "select"}
              />

              {clientTypeError && (
                <p className="create-pendent-load-dropdown-field-error">
                  {t("errors.selectClientType")}
                </p>
              )}

              <div className="create-pendent-load-field-group create-pendent-load-client-dependent-field">
                <label className="create-pendent-load-label">
                  {clientType === "industry"
                    ? t("fields.industryClient")
                    : clientType === "carrier"
                    ? t("fields.carrierClient")
                    : t("fields.industryClient", {
                        defaultValue: "Cliente Indústria*",
                      })}
                </label>

                <Select
                  className="create-pendent-load-fullwidth-select"
                  value={selectedClient}
                  onChange={(selected) => {
                    setSelectedClient(selected);
                    setSelectedClientContactEmails([]);
                    setSelectedClientError(false);
                  }}
                  options={
                    clientType === "industry"
                      ? industryClients.map((c) => ({
                          value: c.id,
                          label: c.companyName || c.name,
                        }))
                      : clientType === "carrier"
                      ? carrierClients.map((c) => ({
                          value: c.id,
                          label: c.companyName || c.name,
                        }))
                      : []
                  }
                  styles={customSelectStyles}
                  placeholder={
                    clientType
                      ? t("placeholders.selectClient")
                      : t("placeholders.selectClientType")
                  }
                  isClearable
                  isDisabled={!clientType}
                  classNamePrefix={
                    selectedClientError ? "select-error" : "select"
                  }
                />

                {selectedClientError && (
                  <p className="create-pendent-load-dropdown-field-error">
                    {t("errors.selectClient")}
                  </p>
                )}
              </div>

              <div className="create-pendent-load-field-group create-pendent-load-client-dependent-field">
                <label className="create-pendent-load-label">
                  {t("fields.clientContactEmails.label", {
                    defaultValue: "Email(s) de contacto do cliente",
                  })}
                </label>

                <Select
                  className="create-pendent-load-fullwidth-select"
                  isMulti
                  isClearable
                  closeMenuOnSelect={false}
                  options={selectedClient ? selectedClientContactEmailOptions : []}
                  value={selectedClientContactEmails}
                  onChange={(selectedOptions) => {
                    const limited = Array.isArray(selectedOptions)
                      ? selectedOptions.slice(0, 3)
                      : [];
                    setSelectedClientContactEmails(limited);
                  }}
                  placeholder={t("fields.clientContactEmails.placeholder", {
                    defaultValue: "Seleciona até 3 emails de contacto...",
                  })}
                  noOptionsMessage={() =>
                    selectedClient
                      ? t("fields.clientContactEmails.noOptions", {
                          defaultValue:
                            "Não foram encontrados emails de contacto para este cliente.",
                        })
                      : t("placeholders.selectClient", {
                          defaultValue: "Seleciona o cliente",
                        })
                  }
                  styles={multiSelectHorizontalStyles}
                  isDisabled={!selectedClient}
                  classNamePrefix="select"
                  menuPortalTarget={document.body}
                />

                <div className="create-pendent-load-helper-text">
                  {t("fields.clientContactEmails.helper", {
                    defaultValue:
                      "Estes emails vêm da lista de contactos do cliente.",
                  })}
                </div>
              </div>

              <div className="create-pendent-load-field-group create-pendent-load-client-dependent-field">
                <label className="create-pendent-load-label">
                  {t("fields.transportCarrier")}
                </label>

                <Select
                  className="create-pendent-load-fullwidth-select"
                  options={availableCarriers}
                  value={
                    availableCarriers.find(
                      (c) => String(c.value) === String(transportCarrierId)
                    ) || null
                  }
                  onChange={(selected) => {
                    setSelectedSubcontractedEmails([]);

                    if (!selected) {
                      const internalOpt = availableCarriers.find(
                        (c) => c?.isInternal || String(c?.value) === "__internal__"
                      );

                      setTransportCarrierId(
                        internalOpt?.value ? String(internalOpt.value) : null
                      );
                    } else {
                      setTransportCarrierId(String(selected.value));
                    }

                    setCarrierError(false);
                  }}
                  isClearable
                  placeholder={t("placeholders.selectTransportCarrier")}
                  styles={customSelectStyles}
                  classNamePrefix={carrierError ? "select-error" : "select"}
                  filterOption={(candidate, input) => {
                    const term = String(input || "").toLowerCase().trim();
                    if (!term) return true;

                    const data = candidate?.data || {};
                    return String(data.searchText || data.label || "")
                      .toLowerCase()
                      .includes(term);
                  }}
                />

                {carrierError && (
                  <p className="create-pendent-load-dropdown-field-error">
                    {t("errors.selectTransportCarrier")}
                  </p>
                )}
              </div>

              {showPrice4subcontracted && (
                <div className="create-pendent-load-field-group create-pendent-load-client-dependent-field">
                  <label className="create-pendent-load-label">
                    {t("fields.subcontractedEmails.label", {
                      defaultValue: "Email(s) do subcontratado",
                    })}
                  </label>

                  <Select
                    className="create-pendent-load-fullwidth-select"
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    options={selectedSubcontractedEmailOptions}
                    value={selectedSubcontractedEmails}
                    onChange={(selectedOptions) => {
                      const limited = Array.isArray(selectedOptions)
                        ? selectedOptions.slice(0, 3)
                        : [];
                      setSelectedSubcontractedEmails(limited);
                    }}
                    placeholder={t("fields.subcontractedEmails.placeholder", {
                      defaultValue:
                        "Seleciona até 3 emails do subcontratado...",
                    })}
                    noOptionsMessage={() =>
                      t("fields.subcontractedEmails.noOptions", {
                        defaultValue:
                          "Não foram encontrados emails de contacto para o transportador selecionado.",
                      })
                    }
                    styles={multiSelectHorizontalStyles}
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                  />

                  <div className="create-pendent-load-helper-text">
                    {t("fields.subcontractedEmails.helper", {
                      defaultValue:
                        "Estes emails vêm da lista de contactos do transportador efetivo selecionado.",
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>


          {/* UNLOADING POINTS */}
          <div className="create-pendent-load-column">
            <h3 className="create-pendent-load-column-title">
              {t("sections.unloadingPoints")}
            </h3>
            {deliveryPoints.map((point, index) => (
              <div
                key={index}
                className="create-pendent-load-point-group"
              >
                {/* Label + Delete */}
                <div className="create-pendent-load-label-row">
                  <label className="create-pendent-load-points-label">
                    {t("fields.deliveryPlace")}
                  </label>
                  {deliveryPoints.length > 1 && (
                    <button
                      type="button"
                      className="create-pendent-load-delete-btn"
                      onClick={() => removeDeliveryPlace(index)}
                      title={t("place.removeDelivery")}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Location Dropdown */}
                <div className="select-wrapper-left-icon">
                  <CreatableSelect
                    isClearable
                    isMulti={false}
                    options={placeOptions}
                    placeholder={t("place.selectOrType")}
                    value={getPlaceValueFromPoint(point, placeOptions)}
                    formatOptionLabel={formatPlaceOptionLabel}
                    onChange={(selectedOption) => {
                      const updated = [...deliveryPoints];
                      const updatedErrors = { ...invalidFields };
                      let newPoint = { ...updated[index] };

                      if (selectedOption?.__isNew__) {
                        newPoint = {
                          ...newPoint,
                          placeId: null,
                          location: selectedOption.label,
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          workingHours: null,
                        };
                      } else if (selectedOption?.fullData) {
                        const p = selectedOption.fullData;
                        newPoint = {
                          ...newPoint,
                          placeId: p.id,
                          location: p.placeName,
                          address: p.address,
                          postalCode: p.postalCode,
                          city: p.city,
                          country: p.country,
                          gMapsLink: p.gMapsLink,
                          coordinates: p.coordinates,
                          contactPhone: p.contactPhone,
                          workingHours: p.workingHours ?? null,
                        };
                      } else {
                        newPoint = {
                          ...newPoint,
                          placeId: null,
                          location: selectedOption?.label || "",
                          address: "",
                          postalCode: "",
                          city: "",
                          country: "",
                          gMapsLink: "",
                          coordinates: "",
                          contactPhone: "",
                          workingHours: null,
                        };
                      }

                      const fieldsToCheck = [
                        newPoint.address,
                        newPoint.postalCode,
                        newPoint.city,
                        newPoint.country,
                        newPoint.gMapsLink,
                        newPoint.coordinates,
                        newPoint.contactPhone,
                      ];
                      newPoint.showMoreFields = fieldsToCheck.some(
                        (v) => !v?.trim()
                      );

                      updated[index] = newPoint;
                      setDeliveryPoints(updated);

                      const fields = [
                        "location",
                        "address",
                        "postalCode",
                        "city",
                        "country",
                        "gMapsLink",
                        "coordinates",
                        "contactPhone",
                      ];
                      fields.forEach(
                        (field) =>
                          delete updatedErrors[`delivery-${field}-${index}`]
                      );
                      setInvalidFields(updatedErrors);

                      const allFilled = updated.every(
                        (d) =>
                          d.location.trim() !== "" &&
                          d.date !== "" &&
                          d.hour !== ""
                      );
                      if (allFilled) setDeliveryPlaceError("");
                    }}
                    styles={customSelectStyles}
                    classNamePrefix={
                      invalidFields[`delivery-location-${index}`]
                        ? "select-error"
                        : "select"
                    }
                  />
                  {invalidFields[`delivery-location-${index}`] && (
                    <p className="dropdown-field-error">
                      {t("errors.locationRequired")}
                    </p>
                  )}
                </div>

                {/* Date + Hour */}
                <div className="create-pendent-load-date-hour-row">
                  <div className="custom-input-wrapper">
                    <input
                      type="date"
                      value={point.date}
                      className={`create-pendent-load-input ${
                        invalidFields[`delivery-date-${index}`]
                          ? "input-error"
                          : ""
                      }`}
                      onChange={(e) => {
                        const updated = [...deliveryPoints];
                        updated[index].date = e.target.value;
                        setDeliveryPoints(updated);
                        if (didSubmit) {
                          const errs = checkDeliveryFieldErrors(updated);
                          setInvalidFields((prev) => ({ ...prev, ...errs }));
                          setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                        }
                        clearFieldErrors(index, "delivery", ["date"]);

                        setTimeout(() => {
                          inputRefs.current[
                            `delivery-date-${index}`
                          ]?.blur();
                        }, 100);
                      }}
                      ref={(el) =>
                        (inputRefs.current[`delivery-date-${index}`] = el)
                      }
                    />
                    <FaCalendarAlt
                      className="input-icon"
                      onClick={() => {
                        closeAllNativePickers(`delivery-date-${index}`);
                        inputRefs.current[`delivery-date-${index}`]?.focus();
                        inputRefs.current[`delivery-date-${index}`]?.showPicker?.();
                      }}
                    />
                  </div>

                  <div className="custom-input-wrapper">
                    <input
                      type="text"
                      placeholder="HH:MM"
                      value={point.hour || ""}
                      className={`create-pendent-load-input ${
                        invalidFields[`delivery-hour-${index}`] || point.hourError
                          ? "input-error"
                          : ""
                      }`}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);

                        let formatted = raw;
                        if (raw.length >= 3) {
                          formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
                        }

                        const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                        const isValid = regex.test(formatted);

                        const updated = [...deliveryPoints];
                        updated[index] = {
                          ...updated[index],
                          hour: formatted,
                          hourError: formatted.length === 5 && !isValid,
                        };

                        setDeliveryPoints(updated);
                        setGlobalHourFormatError(formatted.length === 5 && !isValid);

                        if (didSubmit) {
                          const errs = checkDeliveryFieldErrors(updated);
                          setInvalidFields((prev) => ({ ...prev, ...errs }));
                          setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                        }

                        setInvalidFields((prev) => ({
                          ...prev,
                          [`delivery-hour-${index}`]: !formatted.trim(),
                        }));

                        clearFieldErrors(index, "delivery", ["hour"]);
                      }}
                      ref={(el) =>
                        (inputRefs.current[`delivery-hour-${index}`] = el)
                      }
                    />
                    <FaClock
                      className="input-icon"
                      onClick={() => {
                        inputRefs.current[`delivery-hour-${index}`]?.focus();
                      }}
                    />
                  </div>
                </div>

                {invalidFields[`delivery-date-${index}`] && (
                  <p className="data-error-text">
                    {t("errors.dateRequired")}
                  </p>
                )}
                {invalidFields[`delivery-hour-${index}`] && (
                  <p className="data-error-text">
                    {t("errors.hourRequired")}
                  </p>
                )}

                <div className="create-pendent-load-point-dimensions-block">
                  <label className="create-pendent-load-label create-pendent-load-point-dimensions-title">
                    {t("fields.pointDimensions", { defaultValue: "Dimensões do ponto" })}*
                  </label>

                  <div className="create-pendent-load-point-dimensions-row">
                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointLengthShort", { defaultValue: "Comp." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.length || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "length", e.target.value, false)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`delivery-length-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.lengthError && (
                        <p className="create-pendent-load-error-text">
                          {point.lengthError}
                        </p>
                      )}
                    </div>

                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointWidthShort", { defaultValue: "Larg." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.width || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "width", e.target.value, false)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`delivery-width-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.widthError && (
                        <p className="create-pendent-load-error-text">
                          {point.widthError}
                        </p>
                      )}
                    </div>

                    <div className="create-pendent-load-point-dimension-field">
                      <label>
                        {t("fields.pointHeightShort", { defaultValue: "Alt." })}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={point.height || ""}
                        onChange={(e) =>
                          handlePointDimensionChange(index, "height", e.target.value, false)
                        }
                        className={`create-pendent-load-input ${
                          invalidFields[`delivery-height-${index}`] ? "input-error" : ""
                        }`}
                        placeholder="0.00m"
                      />
                      {point.heightError && (
                        <p className="create-pendent-load-error-text">
                          {point.heightError}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 🆕 Loading Access (below date/hour) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: -5 }}>
                  <label className="create-pendent-load-label">
                    {t("loadingAccess.label", { defaultValue: "Loading access" })}
                  </label>

                  <div className="create-pendent-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={loadingAccessOptions}
                      value={
                        loadingAccessOptions.find(
                          (o) =>
                            o.value ===
                            String(point.loadingAccess || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleLoadingAccessChange(index, opt, false)}
                      isClearable
                      placeholder={t("loadingAccess.placeholder", { defaultValue: "Select loading access..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={"select"}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                {/* 🆕 Handling Equipment (per point) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: 2 }}>
                  <label className="create-pendent-load-label">
                    {t("handlingEquipment.label", { defaultValue: "Handling equipment" })}
                  </label>

                  <div className="create-pendent-load-select-wrapper" style={{ marginBottom: 10 }}>
                    <Select
                      options={handlingEquipmentOptions}
                      value={
                        handlingEquipmentOptions.find(
                          (o) => o.value === String(point.handlingEquipment || "").trim().toUpperCase()
                        ) || null
                      }
                      onChange={(opt) => handleHandlingEquipmentChange(index, opt, false)}
                      isClearable
                      placeholder={t("handlingEquipment.placeholder", { defaultValue: "Select handling equipment..." })}
                      styles={{ ...customSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      classNamePrefix={
                        invalidFields[`delivery-handlingEquipment-${index}`] ? "select-error" : "select"
                      }
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {String(point.handlingEquipment || "").trim().toUpperCase() === "OTHER" && (
                    <>
                      <label className="create-pendent-load-label" style={{ marginTop: 6 }}>
                        {t("handlingEquipment.otherLabel", { defaultValue: "Outro (especificar)" })}
                      </label>

                      <input
                        type="text"
                        value={point.handlingEquipmentOther || ""}
                        placeholder={t("handlingEquipment.otherPlaceholder", { defaultValue: "Please specify..." })}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index] = { ...updated[index], handlingEquipmentOther: e.target.value };
                          setDeliveryPoints(updated);

                          // limpa erro ao escrever
                          setInvalidFields((prev) => {
                            const next = { ...prev };
                            delete next[`delivery-handlingEquipmentOther-${index}`];
                            return next;
                          });
                        }}
                        className={`create-pendent-load-input ${
                          invalidFields[`delivery-handlingEquipmentOther-${index}`] ? "input-error" : ""
                        }`}
                      />
                    </>
                  )}

                  {invalidFields[`delivery-handlingEquipmentOther-${index}`] && (
                    <p className="data-error-text">
                      {t("errors.handlingEquipmentOtherRequired", { defaultValue: "❌ Please specify the handling equipment." })}
                    </p>
                  )}
                </div>

                {/* 🆕 Reference (optional) */}
                <div className="create-pendent-load-field-group" style={{ marginTop: -2 }}>
                  <label className="create-pendent-load-label">
                    {t("fields.reference", { defaultValue: "Reference" })}
                  </label>

                  <input
                    type="text"
                    value={point.reference || ""}
                    placeholder={t("placeholders.reference", {
                      defaultValue: "Optional reference…",
                    })}
                    onChange={(e) => {
                      const updated = [...deliveryPoints];
                      updated[index] = { ...updated[index], reference: e.target.value };
                      setDeliveryPoints(updated);
                    }}
                    className="create-pendent-load-input"
                  />
                </div>

                {/* Extra Fields */}
                {point.showMoreFields ? (
                  <>
                    <div className="create-pendent-load-more-fields">
                      {/* Read-only working hours badge */}
                      {(() => {
                        const dayKey = weekdayKeyFromISO(point.date);
                        const whObj = parseWorkingHours(
                          point.workingHours
                        );
                        const text = formatIntervalsForDay(
                          whObj,
                          dayKey
                        );
                        return (
                          <div className="pendent-load-details-working-hours-badge">
                            <span className="pendent-load-details-working-hours-day">
                              {getWeekdayLabel(dayKey)}
                            </span>
                            <span className="pendent-load-details-working-hours-text">
                              {text ?? t("workingHours.noInfo")}
                            </span>
                          </div>
                        );
                      })()}
                      {/* Linha 1: Address */}
                      <input
                        type="text"
                        placeholder={t("fields.address")}
                        value={point.address || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index].address = e.target.value;
                          setDeliveryPoints(updated);
                          if (didSubmit) {
                            const errs = checkDeliveryFieldErrors(updated);
                            setInvalidFields((prev) => ({ ...prev, ...errs }));
                            setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                          }
                          clearFieldErrors(index, "delivery", [
                            "address",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`delivery-address-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 2: Postal Code, City, Country */}
                      <div className="create-pendent-load-row-tight">
                        <input
                          type="text"
                          placeholder={t("fields.postalCode")}
                          value={point.postalCode || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].postalCode = e.target.value;
                            setDeliveryPoints(updated);
                            if (didSubmit) {
                              const errs = checkDeliveryFieldErrors(updated);
                              setInvalidFields((prev) => ({ ...prev, ...errs }));
                              setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                            }
                            clearFieldErrors(index, "delivery", [
                              "postalCode",
                            ]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`delivery-postalCode-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("fields.city")}
                          value={point.city || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].city = e.target.value;
                            setDeliveryPoints(updated);
                            if (didSubmit) {
                              const errs = checkDeliveryFieldErrors(updated);
                              setInvalidFields((prev) => ({ ...prev, ...errs }));
                              setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                            }
                            clearFieldErrors(index, "delivery", ["city"]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`delivery-city-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />

                        <input
                          type="text"
                          placeholder={t("fields.country")}
                          value={point.country || ""}
                          onChange={(e) => {
                            const updated = [...deliveryPoints];
                            updated[index].country = e.target.value;
                            setDeliveryPoints(updated);
                            if (didSubmit) {
                              const errs = checkDeliveryFieldErrors(updated);
                              setInvalidFields((prev) => ({ ...prev, ...errs }));
                              setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                            }
                            clearFieldErrors(index, "delivery", [
                              "country",
                            ]);
                          }}
                          className={`create-pendent-load-input create-pendent-load-main-field ${
                            invalidFields[`delivery-country-${index}`]
                              ? "input-error"
                              : ""
                          }`}
                        />
                      </div>

                      {/* Linha 3: Contact Phone */}
                      <input
                        type="text"
                        placeholder={t("fields.contactPhone")}
                        value={point.contactPhone || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index].contactPhone = e.target.value;
                          setDeliveryPoints(updated);
                          if (didSubmit) {
                            const errs = checkDeliveryFieldErrors(updated);
                            setInvalidFields((prev) => ({ ...prev, ...errs }));
                            setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                          }
                          clearFieldErrors(index, "delivery", [
                            "contactPhone",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`delivery-contactPhone-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 4: Google Maps Link */}
                      <input
                        type="text"
                        placeholder={t("fields.gMapsLink")}
                        value={point.gMapsLink || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index].gMapsLink = e.target.value;
                          setDeliveryPoints(updated);
                          if (didSubmit) {
                            const errs = checkDeliveryFieldErrors(updated);
                            setInvalidFields((prev) => ({ ...prev, ...errs }));
                            setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                          }
                          clearFieldErrors(index, "delivery", [
                            "gMapsLink",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`delivery-gMapsLink-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />

                      {/* Linha 5: Coordinates */}
                      <input
                        type="text"
                        placeholder={t("fields.coordinates")}
                        value={point.coordinates || ""}
                        onChange={(e) => {
                          const updated = [...deliveryPoints];
                          updated[index].coordinates = e.target.value;
                          setDeliveryPoints(updated);
                          if (didSubmit) {
                            const errs = checkDeliveryFieldErrors(updated);
                            setInvalidFields((prev) => ({ ...prev, ...errs }));
                            setDeliveryFieldValidationError(Object.keys(errs).length > 0);
                          }
                          clearFieldErrors(index, "delivery", [
                            "coordinates",
                          ]);
                        }}
                        className={`create-pendent-load-input create-pendent-load-main-field ${
                          invalidFields[`delivery-coordinates-${index}`]
                            ? "input-error"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="create-pendent-load-toggle-fields-wrapper">
                      <button
                        type="button"
                        className="create-pendent-load-toggle-fields-btn"
                        onClick={() => {
                          const updated = [...deliveryPoints];
                          updated[index].showMoreFields = false;
                          setDeliveryPoints(updated);
                        }}
                      >
                        {t("buttons.hideFields")}{" "}
                        <FiChevronUp className="create-pendent-load-toggle-arrow" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="create-pendent-load-toggle-fields-wrapper">
                    <button
                      type="button"
                      className="create-pendent-load-toggle-fields-btn"
                      onClick={() => {
                        const updated = [...deliveryPoints];
                        updated[index].showMoreFields = true;
                        setDeliveryPoints(updated);
                      }}
                    >
                      {t("buttons.moreFields")}{" "}
                      <FiChevronDown className="create-pendent-load-toggle-arrow" />
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Max limit + add button */}
            {deliveryPoints.length < 7 ? (
              <button
                className="create-pendent-load-add-button"
                onClick={() =>
                  setDeliveryPoints([
                    ...deliveryPoints,
                    {
                      location: "",
                      date: "",
                      hour: "",
                      hourError: false,

                      // 🆕 point-level dimensions
                      length: "",
                      width: "",
                      height: "",
                      lengthError: "",
                      widthError: "",
                      heightError: "",

                      placeId: null,
                      address: "",
                      postalCode: "",
                      city: "",
                      country: "",
                      gMapsLink: "",
                      coordinates: "",
                      contactPhone: "",
                      showMoreFields: false,
                      workingHours: null,
                      loadingAccess: null,
                      handlingEquipment: null,
                      handlingEquipmentOther: "",
                      reference: "",
                    },
                  ])
                }
              >
                {t("buttons.addDelivery")}
              </button>
            ) : (
              <p className="create-pendent-load-warning-text">
                {t("warnings.maxDeliveries")}
              </p>
            )}

            {didSubmit && deliveryFieldValidationError && (
              <p className="create-pendent-load-error-text">
                {t("errors.allDeliveryFieldsRequired", {
                  defaultValue: "❌ All Delivery fields are required.",
                })}
              </p>
            )}

            {/* Global validation errors */}
            {deliveryPlaceError && (
              <p className="create-pendent-load-error-text">
                {deliveryPlaceError}
              </p>
            )}

            {pickupBeforeDeliveryError && (
              <p className="create-pendent-load-error-text">
                {t("errors.deliveryBeforePickup")}
              </p>
            )}

            {globalHourFormatError && (
              <p
                className="create-pendent-load-error-text"
                style={{ marginTop: "1rem" }}
              >
                {t("errors.invalidHourFormat")}
              </p>
            )}

            {error && (
              <p className="create-pendent-load-error-text">{error}</p>
            )}
          </div>
        </div>

        {/* Notes for Driver (optional) */}
        <div className="create-pendent-load-field-group">
          <label className="create-pendent-load-label">
            {t("fields.notesForDriver")}
          </label>
          <textarea
            value={notes4driver}
            onChange={(e) => setNotes4driver(e.target.value)}
            onBlur={() => {
              if (
                typeof notes4driver === "string" &&
                notes4driver.length > 10000
              ) {
                setNotes4driver(notes4driver.slice(0, 10000));
              }
            }}
            maxLength={10000}
            rows={4}
            className="create-pendent-load-textarea"
            placeholder={t("placeholders.notesForDriver")}
          />
          <div className="create-pendent-load-help-text">
            {`${notes4driver?.length || 0}/10000`}
          </div>
        </div>

        {!loading ? (
          <div className="create-pendent-load-button-group">
            <button
              className="create-pendent-load-cancel-button"
              onClick={() =>
                navigate("/loads?view=pendent", { replace: true })
              }
            >
              {t("buttons.cancel")}
            </button>
            <button
              className="create-pendent-load-button"
              onClick={handleCreate}
            >
              {t("buttons.create")}
            </button>
          </div>
        ) : (
          <p className="create-pendent-load-loading-text">
            {t("misc.creating")}
          </p>
        )}
      </div>
    </div>
  );
};

export default CreatePendentLoad;
